import Links from '@/shared/constants/navegacao';
import { Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { EnumerateDefensores } from '@/modules/people/defensor.service';
import { ReadDefensorResponseDto, ReadVinculoAssessorDefensorDto } from '@/api.types';
import { DataAmigavel } from '@/shared/utils/datas';

type LinhaAssessor = {
  defensorId: number;
  defensorNome: string;
  pessoaId?: string;
  assessorNome: string;
  dataInicial: string;
  dataFinal: string | null;
  vigente: boolean;
};

function toDateKey(value?: string | Date | null): string | null {
  if (value == null || value === '') return null;
  const s = String(value);
  const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function vinculoVigente(vinculo: ReadVinculoAssessorDefensorDto, hoje: string): boolean {
  const inicio = toDateKey(vinculo.data_inicial);
  if (!inicio || inicio > hoje) return false;
  const fim = toDateKey(vinculo.data_final);
  return !fim || fim >= hoje;
}

function montarLinhas(defensores: ReadDefensorResponseDto[]): LinhaAssessor[] {
  const hoje = new Date().toISOString().slice(0, 10);
  const linhas: LinhaAssessor[] = [];

  for (const defensor of defensores) {
    const vinculos = defensor.vinculosAssessores ?? [];
    if (vinculos.length === 0) continue;

    const defensorNome = defensor.pessoa?.nome?.trim() || `Defensor #${defensor.id}`;
    for (const vinculo of vinculos) {
      linhas.push({
        defensorId: defensor.id,
        defensorNome,
        pessoaId: defensor.pessoa?.id,
        assessorNome:
          vinculo.colaborador_nome?.trim() ||
          (vinculo.colaborador_id ? `Assessor #${vinculo.colaborador_id}` : '-'),
        dataInicial: String(vinculo.data_inicial ?? ''),
        dataFinal: vinculo.data_final ? String(vinculo.data_final) : null,
        vigente: vinculoVigente(vinculo, hoje),
      });
    }
  }

  return linhas.sort((a, b) => {
    const porDefensor = a.defensorNome.localeCompare(b.defensorNome, 'pt-BR');
    if (porDefensor !== 0) return porDefensor;
    return a.dataInicial.localeCompare(b.dataInicial);
  });
}

export default function DefensoresAssessoresPage() {
  const { setActions } = useBreadcrumbActions();
  const [linhas, setLinhas] = useState<LinhaAssessor[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setActions({
      primary: { label: 'Voltar', onClick: () => {}, href: Links.Pessoa_Index },
    });
    return () => setActions({});
  }, [setActions]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const resultado = await EnumerateDefensores(0, 10000);
      setLinhas(montarLinhas(resultado.objects));
    } catch (e) {
      setLinhas([]);
      setErro(e instanceof Error ? e.message : 'Falha ao carregar defensores e assessores.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const totalDefensores = useMemo(
    () => new Set(linhas.map((linha) => linha.defensorId)).size,
    [linhas],
  );

  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2">
          <h3 className="mb-0">Defensores e Assessores</h3>
        </div>
        <span className="blockquote-footer pb-4 text-end">
          Defensores com pelo menos um assessor vinculado.
        </span>
      </div>

      <hr className="pb-4" />

      {loading ? (
        <div className="col-12 text-center py-5">
          <Spinner animation="border" role="status" />
          <p className="text-muted mt-2 mb-0">Carregando vínculos…</p>
        </div>
      ) : erro ? (
        <div className="col-12">
          <div className="alert alert-danger mb-0">{erro}</div>
        </div>
      ) : linhas.length === 0 ? (
        <div className="col-12">
          <div className="alert alert-secondary mb-0">
            Nenhum defensor com assessor vinculado foi encontrado.
          </div>
        </div>
      ) : (
        <div className="col-12">
          <p className="text-muted small mb-2">
            {totalDefensores} defensor(es) · {linhas.length} vínculo(s)
          </p>
          <Table striped bordered hover size="sm" responsive className="mb-0">
            <thead>
              <tr>
                <th>Defensor(a)</th>
                <th>Assessor(a)</th>
                <th className="text-center" style={{ width: '8rem' }}>
                  Início
                </th>
                <th className="text-center" style={{ width: '8rem' }}>
                  Fim
                </th>
                <th className="text-center" style={{ width: '7rem' }}>
                  Situação
                </th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha, index) => (
                <tr key={`${linha.defensorId}-${linha.assessorNome}-${linha.dataInicial}-${index}`}>
                  <td>
                    {linha.pessoaId ? (
                      <Link to={`${Links.Pessoa_Editar}/${linha.pessoaId}`}>{linha.defensorNome}</Link>
                    ) : (
                      linha.defensorNome
                    )}
                  </td>
                  <td>{linha.assessorNome}</td>
                  <td className="text-center">
                    {linha.dataInicial ? DataAmigavel(linha.dataInicial) : '-'}
                  </td>
                  <td className="text-center">
                    {linha.dataFinal ? DataAmigavel(linha.dataFinal) : 'Em aberto'}
                  </td>
                  <td className="text-center">
                    {linha.vigente ? (
                      <span className="badge bg-success">Vigente</span>
                    ) : (
                      <span className="badge bg-secondary">Encerrado</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}
